import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { formatDateDDMMYYYY, getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import NoRecentDocumentsFoundIcon from '../../../assets/images/no-recent-documents-found-icon.png'

import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { Button } from '../../../components/Button/Button'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { type FileBrowserItem } from '../../../components/DocumentFileBrowser/types'

import { DocumentRecord } from '../../../models/Document'
import { User } from '../../../models/User'

import { type IntegrationServiceId, getIntegrationDisplayName } from '../../../common/integrations'

import { setDocumentContactsFilter } from '../../../store/processDocument/documentFilter'
import { useGetExternalUserByIdQuery } from '../../../store/externalUser/externalUser'
import { useProcessDocumentsMutation } from '../../../store/ai/documents'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetUsersQuery } from '../../../store/user/user'
import { addToast } from '../../../store/toast/toastSlice'

import { ProcessDocumentFileInput } from '../ProcessDocumentFileInput/ProcessDocumentFileInput'
import { DocumentProcessingStatus } from '../DocumentProcessingStatus/DocumentProcessingStatus'
import {
  DocumentUploadQueueItem,
  EmailAttachmentDocumentUploadQueueItem,
  EmailAttachmentToAdd,
  IntegrationDocumentUploadQueueItem,
  useDocumentFile,
  useDocumentFileUpload,
} from '../hooks'
import { DocumentFileIcon } from '../DocumentFileIcon'

import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

import { ContactDocumentBrowseButtons } from './ContactDocumentBrowseButtons'
import { EmailAttachmentSuggestions } from './EmailAttachmentSuggestions'
import { useDocuments, useExistingDocument } from './hooks'
import {
  DOCUMENT_UPLOAD_SOURCE_LABEL,
  DocumentUploadSource,
  formatFileSize,
  isFileSizeValid,
  MAX_DOCUMENT_BATCH_FILES,
  MAX_DOCUMENT_FILE_SIZE_BYTES,
  zocksUserToSelectOption,
} from './utils'

import './DocumentUpload.scss'
import { skipToken } from '@reduxjs/toolkit/query'

const summarizeBatchSources = (items: DocumentUploadQueueItem[]) => {
  const counts = items.reduce(
    (counts, item) => {
      counts[item.source] += 1
      return counts
    },
    { [DocumentUploadSource.ZOCKS]: 0, [DocumentUploadSource.INTEGRATION]: 0, [DocumentUploadSource.EMAIL_ATTACHMENT]: 0 },
  )

  const presentSources = Object.values(DocumentUploadSource).filter((source) => counts[source] > 0)

  return {
    source: presentSources.length === 1 ? DOCUMENT_UPLOAD_SOURCE_LABEL[presentSources[0]] : 'mixed',
    manualUploadCount: counts[DocumentUploadSource.ZOCKS],
    integrationCount: counts[DocumentUploadSource.INTEGRATION],
    emailAttachmentCount: counts[DocumentUploadSource.EMAIL_ATTACHMENT],
  }
}

interface DocumentUploadProps {
  initialContactId?: string | null
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({ initialContactId = null }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)

  const navigate = useNavigate()

  const { tracked, trackingClient } = useTracking()

  const [triggerProcessDocuments, { isLoading: isProcessing }] = useProcessDocumentsMutation()

  const { uploadQueueItems, readyCount, allItemsReady, onFilesSelected, onIntegrationFilesSelected, onEmailAttachmentsSelected, onClear, onClearAll, onRetry } =
    useDocumentFileUpload()

  const { quotaSuccess: emailQuotaSuccess, isFetching: emailQuotaFetching, hasQuota } = useZocksQuota()
  const emailAttachmentSuggestionsEnabled = (emailQuotaSuccess || !emailQuotaFetching) && hasQuota('integration.emailEnabled.value')

  const [pickedContact, setPickedContact] = useState<User | null>(null)

  const { data: initialContactResponse } = useGetExternalUserByIdQuery(initialContactId ? { accountId, userId: initialContactId } : skipToken)
  const initialContact = initialContactResponse?.data ?? null

  const contact = pickedContact ?? initialContact

  useEffect(() => {
    if (!initialContact) {
      return
    }

    dispatch(setDocumentContactsFilter([initialContact.id]))
  }, [initialContact, dispatch])

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      const remaining = MAX_DOCUMENT_BATCH_FILES - uploadQueueItems.length
      const acceptedFiles = files.slice(0, remaining)

      if (files.length > remaining) {
        dispatch(addToast({ variant: 'error', title: 'Too many files', messages: [`You can upload at most ${MAX_DOCUMENT_BATCH_FILES} files per batch.`] }))
      }

      const validFiles = acceptedFiles.filter((file) => isFileSizeValid(file.size))

      if (validFiles.length < acceptedFiles.length) {
        dispatch(
          addToast({
            variant: 'error',
            title: 'Invalid file size',
            messages: [`Each file must not be empty and no larger than ${formatFileSize(MAX_DOCUMENT_FILE_SIZE_BYTES)}.`],
          }),
        )
      }

      const oversizedFiles = acceptedFiles.filter((file) => file.size > MAX_DOCUMENT_FILE_SIZE_BYTES)

      if (oversizedFiles.length) {
        trackingClient.track({
          eventName: TrackEvent.DOCUMENT_FILE_TOO_LARGE,
          eventDetails: {
            rejectedFileCount: oversizedFiles.length,
            largestFileSizeBytes: Math.max(...oversizedFiles.map((file) => file.size)),
            maxFileSizeBytes: MAX_DOCUMENT_FILE_SIZE_BYTES,
            source: DOCUMENT_UPLOAD_SOURCE_LABEL[DocumentUploadSource.ZOCKS],
            context: initialContactId ? 'contact_details' : 'web',
          },
        })
      }

      if (!validFiles.length) {
        return
      }

      await onFilesSelected(validFiles)
    },
    [dispatch, initialContactId, onFilesSelected, trackingClient, uploadQueueItems.length],
  )

  const handleIntegrationFilesSelected = useCallback(
    (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => {
      const files = items.filter((item) => item.type === 'file')
      const remaining = MAX_DOCUMENT_BATCH_FILES - uploadQueueItems.length

      if (files.length > remaining) {
        dispatch(addToast({ variant: 'error', title: 'Too many files', messages: [`You can upload at most ${MAX_DOCUMENT_BATCH_FILES} files per batch.`] }))
      }

      if (remaining <= 0) {
        return
      }

      const validFiles = files.filter((item) => item.size == null || isFileSizeValid(item.size))

      if (validFiles.length < files.length) {
        dispatch(
          addToast({
            variant: 'error',
            title: 'Invalid file size',
            messages: [`Each file must not be empty and no larger than ${formatFileSize(MAX_DOCUMENT_FILE_SIZE_BYTES)}.`],
          }),
        )
      }

      const oversizedFiles = files.filter((item) => item.size != null && item.size > MAX_DOCUMENT_FILE_SIZE_BYTES)

      if (oversizedFiles.length) {
        trackingClient.track({
          eventName: TrackEvent.DOCUMENT_FILE_TOO_LARGE,
          eventDetails: {
            rejectedFileCount: oversizedFiles.length,
            largestFileSizeBytes: Math.max(...oversizedFiles.map((item) => item.size ?? 0)),
            maxFileSizeBytes: MAX_DOCUMENT_FILE_SIZE_BYTES,
            source: DOCUMENT_UPLOAD_SOURCE_LABEL[DocumentUploadSource.INTEGRATION],
            serviceId,
            context: initialContactId ? 'contact_details' : 'web',
          },
        })
      }

      if (!validFiles.length) {
        return
      }

      onIntegrationFilesSelected(serviceId, validFiles)
    },
    [dispatch, initialContactId, onIntegrationFilesSelected, trackingClient, uploadQueueItems.length],
  )

  const handleEmailAttachmentsSelected = useCallback(
    async (attachments: EmailAttachmentToAdd[]): Promise<number> => {
      const remaining = MAX_DOCUMENT_BATCH_FILES - uploadQueueItems.length

      if (attachments.length > remaining) {
        dispatch(addToast({ variant: 'error', title: 'Too many files', messages: [`You can upload at most ${MAX_DOCUMENT_BATCH_FILES} files per batch.`] }))
      }

      if (remaining <= 0) {
        return 0
      }

      return onEmailAttachmentsSelected(attachments)
    },
    [dispatch, onEmailAttachmentsSelected, uploadQueueItems.length],
  )

  const _handleProcess = useCallback(async () => {
    if (!contact || !allItemsReady || isProcessing) {
      return
    }

    const documentIds = uploadQueueItems.flatMap((item) => (item.documentId ? [item.documentId] : []))
    if (!documentIds.length) {
      return
    }

    try {
      const documents = await triggerProcessDocuments({ accountId, payload: { documentIds, contactId: contact.id } }).unwrap()
      if (!documents.length) {
        return
      }

      const documentBatchId = documents[0].documentBatchId

      navigate(documentBatchId ? `/document-batches/${documentBatchId}` : `/documents/${documents[0].id}`)
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
    }
  }, [accountId, allItemsReady, contact, isProcessing, navigate, triggerProcessDocuments, uploadQueueItems])

  const handleProcess = tracked(
    _handleProcess,
    {
      eventName: TrackEvent.PROCESS_DOCUMENTS_CLICKED,
      eventDetails: { documentCount: readyCount, context: initialContactId ? 'contact_details' : 'web' },
    },
    { eventDetailsExtractor: () => summarizeBatchSources(uploadQueueItems) },
  )

  const handleContactSelected = (newContact: User | null) => {
    if (uploadQueueItems.length > 0 && (newContact?.id ?? null) !== (contact?.id ?? null)) {
      void onClearAll()
    }

    setPickedContact(newContact)
    dispatch(setDocumentContactsFilter(newContact ? [newContact.id] : []))
  }

  if (initialContactId && !initialContact) {
    return null
  }

  return (
    <Container className='m-0 p-0' fluid>
      <Row className='m-0 flex-nowrap document-upload-row'>
        <Col className='zocks-p-2.0 d-flex justify-content-center'>
          <div className='d-flex flex-column align-items-center zocks-gap-2 document-upload-left-inner-container'>
            <div className='d-flex flex-column align-items-center zocks-gap-0.5 document-summary-file-upload-header'>
              <h3>Document Intelligence</h3>
              <span className='paragraph-medium text-center'>
                Select a client and upload their documents. The AI Assistant will extract key insights and prepare updates for your CRM and financial systems.
              </span>
            </div>
            <DocumentUploadContactSelector selectedUser={contact} onUserSelected={handleContactSelected} disabled={!!initialContactId} />
            <ProcessDocumentFileInput
              onFilesSelected={handleFilesSelected}
              multiple={true}
              disabled={uploadQueueItems.length >= MAX_DOCUMENT_BATCH_FILES}
              browseAction={
                <ContactDocumentBrowseButtons contact={contact} onContactUpdated={setPickedContact} onFilesSelected={handleIntegrationFilesSelected} />
              }
            />
            {emailAttachmentSuggestionsEnabled ? (
              <EmailAttachmentSuggestions
                contact={contact}
                queueItems={uploadQueueItems}
                disabled={uploadQueueItems.length >= MAX_DOCUMENT_BATCH_FILES}
                onAdd={handleEmailAttachmentsSelected}
              />
            ) : null}
            {uploadQueueItems.length ? (
              <>
                <div className='d-flex flex-column gap-3 w-100'>
                  <h5>Documents to process</h5>
                  {uploadQueueItems.map((item) => (
                    <DocumentUploadQueueRow key={item.id} contact={contact} item={item} onClear={() => onClear(item.id)} onRetry={() => onRetry(item.id)} />
                  ))}
                </div>
                <Tooltip id='process-documents-tooltip' title={!contact ? 'Select a client before processing' : undefined}>
                  <Button variant='primary' onClick={handleProcess} loading={isProcessing} disabled={!contact || !allItemsReady}>
                    Process Documents
                  </Button>
                </Tooltip>
              </>
            ) : null}
          </div>
        </Col>
        <Col xs='auto' className='p-0 zocks-bg-color-gray-100 zocks-border-color-gray-300 border-start document-upload-right-container'>
          <DocumentUploadRecentDocuments contact={contact} />
        </Col>
      </Row>
    </Container>
  )
}

interface DocumentUploadContactSelectorProps {
  selectedUser: User | null
  onUserSelected: (user: User | null) => void
  disabled?: boolean
}

const DocumentUploadContactSelector: React.FC<DocumentUploadContactSelectorProps> = ({ selectedUser, onUserSelected, disabled = false }) => {
  const accountId = useAppSelector(selectAccountId)

  const [searchInput, setSearchInput] = useState<string | null>(null)

  const { data: usersResponse } = useGetUsersQuery(
    { accountId, identityTypes: ['external'], pageRequest: { size: 50, page: 0 }, ...(searchInput ? { name: searchInput } : {}) },
    { refetchOnMountOrArgChange: true },
  )

  const userOptions = useMemo(() => (usersResponse ? usersResponse.data.map(zocksUserToSelectOption) : []), [usersResponse])

  const selectedOption = useMemo(() => (selectedUser ? zocksUserToSelectOption(selectedUser) : null), [selectedUser])

  const handleSearchInputChange = async (newSearchInput: string) => {
    setSearchInput(newSearchInput ?? null)
  }

  const handleOptionSelected = (selectOption: SelectOption) => {
    if (!usersResponse) {
      return
    }

    const user = usersResponse.data.find((e) => e.id === selectOption.id)
    if (!user) {
      return
    }

    onUserSelected(user)
  }

  return (
    <SelectDropdown
      options={userOptions}
      className='zocks-border-color-gray-500'
      initialSelectedOption={selectedOption}
      onSearchInputChange={handleSearchInputChange}
      onOptionSelected={handleOptionSelected}
      disabled={disabled}
      placeholder='Select client'
      hideClearButton={true}
      searchable={true}
      inputAutoComplete='off'
      inputName='documentUploadContactSelector'
    />
  )
}

interface DocumentUploadQueueRowProps {
  contact: User | null
  item: DocumentUploadQueueItem
  onClear: () => void
  onRetry: () => void
}

const DocumentUploadQueueRow: React.FC<DocumentUploadQueueRowProps> = ({ contact, item, onClear, onRetry }) => {
  if (item.source === DocumentUploadSource.INTEGRATION) {
    return <IntegrationDocumentUploadQueueRow item={item} onClear={onClear} onRetry={onRetry} />
  }

  if (item.source === DocumentUploadSource.EMAIL_ATTACHMENT) {
    return <EmailAttachmentDocumentUploadQueueRow item={item} onClear={onClear} onRetry={onRetry} />
  }

  const { file, isUploading, isUploaded, progress = 0, hash } = item

  const clampedProgress = Math.min(100, Math.max(0, progress))

  return (
    <div className='px-3 zocks-py-0.75 d-flex align-items-center gap-3 zocks-border-0.75 border zocks-border-color-gray-400'>
      <div className='document-upload-queue-icon-container d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 border zocks-border-color-gray-300'>
        <DocumentFileIcon contentType={file.type} className='zocks-text-gray-700 fs-5' />
      </div>
      <div className='d-flex flex-column gap-1 flex-grow-1 mw-0'>
        <div className='d-flex align-items-center flex-grow-1 gap-3 mw-0'>
          <span className='bold text-truncate mw-0'>{file.name}</span>
          <span className='paragraph-small zocks-text-gray-700 text-nowrap flex-shrink-0'>({formatFileSize(file.size)})</span>
        </div>
        {!isUploading && contact ? <FileExistsWithNameOrContent contact={contact} fileName={file.name} fileHash={hash} /> : null}
      </div>
      {isUploading ? (
        <div className='document-upload-progress-track zocks-bg-color-gray-400 zocks-border-0.25 flex-shrink-0'>
          <div className='document-upload-progress-fill h-100 zocks-border-0.25' style={{ width: `${clampedProgress}%` }} />
        </div>
      ) : !isUploaded ? (
        <div className='d-flex align-items-center gap-2 paragraph-small flex-shrink-0'>
          <span className='zocks-text-danger text-nowrap flex-shrink-0'>Upload failed</span>
          <span className='bold text-decoration-underline cursor-pointer' onClick={onRetry}>
            Retry
          </span>
        </div>
      ) : null}
      <i className='bi bi-x fs-6 cursor-pointer flex-shrink-0' onClick={onClear} />
    </div>
  )
}

interface IntegrationDocumentUploadQueueRowProps {
  item: IntegrationDocumentUploadQueueItem
  onClear: () => void
  onRetry: () => void
}

const IntegrationDocumentUploadQueueRow: React.FC<IntegrationDocumentUploadQueueRowProps> = ({ item, onClear, onRetry }) => {
  return (
    <div className='px-3 zocks-py-0.75 d-flex align-items-center gap-3 zocks-border-0.75 border zocks-border-color-gray-400'>
      <div className='document-upload-queue-icon-container d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 border zocks-border-color-gray-300'>
        <IntegrationIcon serviceId={item.serviceId} width={20} height={20} />
      </div>
      <div className='d-flex flex-column gap-1 flex-grow-1 mw-0'>
        <div className='d-flex align-items-center flex-grow-1 gap-3 mw-0'>
          <span className='bold text-truncate mw-0'>{item.name}</span>
          <span className='paragraph-small zocks-text-gray-700 text-nowrap flex-shrink-0'>({formatFileSize(item.size)})</span>
        </div>
        <span className='paragraph-extra-small zocks-text-gray-600'>from {getIntegrationDisplayName(item.serviceId)}</span>
      </div>
      {item.error ? (
        <div className='d-flex align-items-center gap-2 paragraph-small flex-shrink-0'>
          <span className='zocks-text-danger text-nowrap flex-shrink-0'>Upload failed</span>
          <span className='bold text-decoration-underline cursor-pointer' onClick={onRetry}>
            Retry
          </span>
        </div>
      ) : null}
      <i className='bi bi-x fs-6 cursor-pointer flex-shrink-0' onClick={onClear} />
    </div>
  )
}

interface EmailAttachmentDocumentUploadQueueRowProps {
  item: EmailAttachmentDocumentUploadQueueItem
  onClear: () => void
  onRetry: () => void
}

const EmailAttachmentDocumentUploadQueueRow: React.FC<EmailAttachmentDocumentUploadQueueRowProps> = ({ item, onClear, onRetry }) => {
  return (
    <div className='px-3 zocks-py-0.75 d-flex align-items-center gap-3 zocks-border-0.75 border zocks-border-color-gray-400'>
      <div className='document-upload-queue-icon-container d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 border zocks-border-color-gray-300'>
        <i className='bi bi-envelope zocks-text-gray-700 fs-5' />
      </div>
      <div className='d-flex align-items-center flex-grow-1 gap-3 mw-0'>
        <span className='bold text-truncate mw-0'>{item.name}</span>
        <span className='paragraph-small zocks-text-gray-700 text-nowrap flex-shrink-0'>({formatFileSize(item.size)})</span>
      </div>
      {item.error ? (
        <div className='d-flex align-items-center gap-2 paragraph-small flex-shrink-0'>
          <span className='zocks-text-danger text-nowrap flex-shrink-0'>Failed to add</span>
          <span className='bold text-decoration-underline cursor-pointer' onClick={onRetry}>
            Retry
          </span>
        </div>
      ) : null}
      <i className='bi bi-x fs-6 cursor-pointer flex-shrink-0' onClick={onClear} />
    </div>
  )
}

interface FileExistsWithNameOrContentProps {
  contact: User
  fileName: string
  fileHash: string
}

const FileExistsWithNameOrContent: React.FC<FileExistsWithNameOrContentProps> = ({ contact, fileName, fileHash }) => {
  const { document, matchedBy, isLoading } = useExistingDocument(contact.id, fileName, fileHash)

  if (!document || !matchedBy || isLoading) {
    return null
  }

  const matchTypeText = matchedBy === 'both' ? 'name and content' : matchedBy === 'name' ? 'name' : 'content'

  return (
    <div className='d-flex align-items-center zocks-gap-0.125 paragraph-extra-small bold'>
      <span className='zocks-text-gray-600'>
        Another file with the same {matchTypeText} is uploaded for {getUserDisplayName(contact)}.
      </span>
      <a
        className='text-decoration-underline text-nowrap zocks-text-gray-900'
        href={`/dashboard/documents/${document.id}`}
        target='_blank'
        rel='noopener noreferrer'
      >
        Review here
      </a>
    </div>
  )
}

interface DocumentUploadRecentDocumentsProps {
  contact: User | null
}

const DocumentUploadRecentDocuments: React.FC<DocumentUploadRecentDocumentsProps> = ({ contact }) => {
  const navigate = useNavigate()

  const { groupedDocuments, isLoading, accountHasZDRPolicy } = useDocuments()

  const entries = Object.entries(groupedDocuments)

  const handleShowAll = () => {
    navigate('/documents')
  }

  return (
    <div className='zocks-p-2.0 d-flex flex-column gap-3'>
      <div className='d-flex flex-column'>
        <div className='d-flex align-items-center justify-content-between gap-3'>
          <h5>Your Recent Documents</h5>
          <div className='zocks-px-0.75 py-2 d-flex align-items-center gap-2 cursor-pointer' onClick={handleShowAll}>
            <span className='paragraph-medium bold'>Show all</span>
            <i className='bi bi-chevron-right fs-6' />
          </div>
        </div>
        {contact ? <span className='paragraph-small zocks-text-gray-700'>for {getUserDisplayName(contact)}</span> : null}
      </div>
      <div className='d-flex flex-column zocks-gap-0.75'>
        {!isLoading ? (
          !entries.length ? (
            <NoRecentDocumentsFound contact={contact} accountHasZDRPolicy={accountHasZDRPolicy} />
          ) : (
            entries.map(([groupId, documents]) => <DocumentUploadRecentDocumentGroup key={groupId} documents={documents} />)
          )
        ) : null}
      </div>
    </div>
  )
}

interface DocumentUploadRecentDocumentGroupProps {
  documents: DocumentRecord[]
}

const DocumentUploadRecentDocumentGroup: React.FC<DocumentUploadRecentDocumentGroupProps> = ({ documents }) => {
  const accountId = useAppSelector(selectAccountId)

  const { createdAt, contactId } = documents[0]

  const { data: contactResponse } = useGetExternalUserByIdQuery(contactId ? { accountId, userId: contactId } : skipToken)

  const contact = contactResponse?.data ?? null

  return (
    <React.Fragment>
      <div className='d-flex align-items-center justify-content-between gap-3'>
        <span className='lead-small'>{contact ? `${formatDateDDMMYYYY(createdAt)} - ${getUserDisplayName(contact)}` : formatDateDDMMYYYY(createdAt)}</span>
        {documents.length > 1 ? <span className='paragraph-extra-small zocks-text-gray-700'>{documents.length} files</span> : null}
      </div>
      {documents.map((document) => (
        <DocumentUploadRecentDocumentRow key={document.id} document={document} />
      ))}
    </React.Fragment>
  )
}

interface DocumentUploadRecentDocumentRowProps {
  document: DocumentRecord
}

const DocumentUploadRecentDocumentRow: React.FC<DocumentUploadRecentDocumentRowProps> = ({ document }) => {
  const { files } = document

  const navigate = useNavigate()

  const { fileInfo, isLoading } = useDocumentFile(files)

  const handleNavigate = () => {
    navigate(`/documents/${document.id}`)
  }

  return (
    <div
      className='document-upload-recent-document-row cursor-pointer px-3 zocks-py-0.75 border zocks-border-0.75 zocks-border-color-gray-400 d-flex align-items-center justify-content-between gap-3 zocks-bg-color-white'
      onClick={handleNavigate}
    >
      <div className='d-flex flex-column gap-1 mw-0'>
        <span className='paragraph-medium bold text-truncate'>{document.name}</span>
        <div className='d-flex align-items-center gap-3'>
          <DocumentProcessingStatus document={document} />
          {!isLoading && !fileInfo ? (
            <div className='d-flex align-items-center gap-1 zocks-text-gray-700 flex-shrink-0'>
              <i className='bi bi-file-earmark-x fs-6' />
              <span className='paragraph-extra-small text-nowrap'>File deleted</span>
            </div>
          ) : null}
        </div>
      </div>
      <i className='flex-shrink-0 bi bi-chevron-right fs-6' />
    </div>
  )
}

interface NoRecentDocumentsFoundProps {
  contact: User | null
  accountHasZDRPolicy: boolean
}

const NoRecentDocumentsFound: React.FC<NoRecentDocumentsFoundProps> = ({ contact, accountHasZDRPolicy }) => {
  return (
    <div className='d-flex flex-column align-items-center gap-3 zocks-pt-4 document-upload-no-documents-found-container'>
      <img alt='No documents found' src={NoRecentDocumentsFoundIcon} />
      <div className='text-center'>
        <h5>No documents found</h5>
        {accountHasZDRPolicy ? (
          <span className='paragraph-medium zocks-text-gray-600'>
            Your account has zero data retention policy for documents, so Zocks doesn't store any of them.
          </span>
        ) : (
          <span className='paragraph-medium zocks-text-gray-600'> There are no documents uploaded{contact ? ` for ${getUserDisplayName(contact)}` : ''}</span>
        )}
      </div>
    </div>
  )
}
